import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@mlm/ui';
import { formatInventoryNote } from '@mlm/shared';
import { listInventoryMovements } from '../lib/api';
import { startOfLocalDay, endOfLocalDay } from '../lib/day';

/**
 * Labels for `inventory_movements.reason`. Unknown values fall through to the
 * raw enum string, so a new reason never renders blank.
 */
// eslint-disable-next-line react-refresh/only-export-components -- long-standing shared map; splitting it out is churn for a cold path
export const REASON_LABEL: Record<string, string> = {
  receive: 'Receive',
  adjustment: 'Adjustment',
  write_off: 'Write-Off',
  void: 'Void',
  pos_sale: 'POS sale',
  ecom_sale: 'Ecom sale',
  refund_restock: 'Refund restock',
  cancel_restock: 'Cancel restock',
  stockist_purchase: 'Stockist purchase',
  consignment_out: 'Consigned out',
  consignment_return: 'Consignment return',
  supplier_return: 'Return to supplier',
  stockist_consign_in: 'Consigned in (stockist)',
  stockist_consign_return: 'Consignment return (stockist)',
  bundle_open: 'Opened bundle',
  material_consume: 'Used in a sale',
  material_restock: 'Returned from a sale',
};

const REASON_OPTIONS = Object.entries(REASON_LABEL);

/**
 * The append-only stock-movement ledger for a branch — answers "paano
 * nabawasan, saan, sino": every POS sale, refund restock, receive, write-off
 * and adjustment, with before → after balances and the acting user. Product-
 * scoped when opened from a row's "View log"; branch-wide (with reason/date
 * filters) on the All movements card. Read-only by design — adjustments stay
 * an admin action.
 */
export function InventoryLog({
  branchId,
  productId,
  showFilters = false,
}: {
  branchId: string;
  productId?: string;
  /** Branch-wide mode: reason + local-day date filters. */
  showFilters?: boolean;
}) {
  const [reason, setReason] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = useQuery({
    queryKey: ['pos-inventory-log', branchId, productId ?? null, reason, from, to],
    queryFn: () =>
      listInventoryMovements({
        branchId,
        productId,
        reason: reason || undefined,
        from: from ? startOfLocalDay(from).toISOString() : undefined,
        to: to ? endOfLocalDay(to).toISOString() : undefined,
      }),
  });

  return (
    <div className="space-y-3">
      {showFilters && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Reason</label>
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">All</option>
              {REASON_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              className="h-9"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" className="h-9" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </div>
      )}
      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !query.data?.length ? (
        <p className="text-sm text-muted-foreground">No movements yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-2 py-1.5">When</th>
                {!productId && <th className="px-2 py-1.5">Product</th>}
                <th className="px-2 py-1.5">Reason</th>
                <th className="px-2 py-1.5 text-right">Δ</th>
                <th className="px-2 py-1.5 text-right">Before → After</th>
                <th className="px-2 py-1.5">By</th>
                <th className="px-2 py-1.5">Memo / Ref</th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((m) => (
                <tr key={m.id} className="border-b last:border-0">
                  <td className="whitespace-nowrap px-2 py-1.5 text-xs text-muted-foreground">
                    {new Date(m.createdAt).toLocaleString()}
                  </td>
                  {!productId && (
                    <td className="px-2 py-1.5 text-xs">
                      {m.productName}
                      {m.variantName && (
                        <span className="text-muted-foreground"> · {m.variantName}</span>
                      )}
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-xs">
                    {REASON_LABEL[m.reason] ?? m.reason}
                    {productId && m.variantName && (
                      <div className="text-[11px] text-muted-foreground">{m.variantName}</div>
                    )}
                  </td>
                  <td
                    className={`px-2 py-1.5 text-right font-medium tabular-nums ${
                      m.delta < 0 ? 'text-destructive' : 'text-success'
                    }`}
                  >
                    {m.delta > 0 ? `+${m.delta}` : m.delta}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                    {m.balanceBefore} → {m.balanceAfter}
                  </td>
                  <td className="px-2 py-1.5 text-xs">{m.actorName ?? m.actorEmail ?? '—'}</td>
                  <td className="max-w-[16rem] truncate px-2 py-1.5 text-xs text-muted-foreground">
                    {formatInventoryNote(m.note) ||
                      (m.refType ? `${m.refType}${m.refId ? ` ${m.refId.slice(0, 8)}` : ''}` : '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
